import { X } from 'lucide-react';
import { useState } from 'react';

import { addSearchTitle } from '@/modules/jobs/signal/searchTitles';
import { Input } from '@/modules/ui/components/input/Input';

/**
 * A job's extra search titles: the search keeps to people with one of the job's titles, so these widen it to people
 * doing the same work under another title. Enter adds what's typed; each title is a chip to remove.
 */
export function JobSearchTitlesField({
  value,
  onChange,
  helpClassName,
}: {
  value: string[];
  onChange: (titles: string[]) => void;
  helpClassName?: string;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const added = addSearchTitle(value, draft);
    setError(added.error ?? null);
    if (added.error) return;
    if (added.titles !== value) onChange(added.titles);
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        aria-label="Extra search titles"
        aria-describedby="job-search-titles-help"
        placeholder="Add a title and press Enter"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
      {value.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {value.map((title) => (
            <span
              key={title}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-400 py-1 pr-1 pl-2.5 text-sm"
            >
              <span className="text-foreground">{title}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((t) => t !== title))}
                aria-label={`Remove ${title}`}
                className="text-neutral-650 hover:text-foreground rounded-full p-0.5"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <p id="job-search-titles-help" className={helpClassName}>
        {error ??
          'Optional. Other titles people doing this job hold, like Lifecycle Marketer for a Growth Marketer.'}
      </p>
    </div>
  );
}
